/**
 * TradingChart - Professional Trading Chart with Light/Dark Theme Support
 * Features: Theme toggle, Pattern visualization, Indicators, Clean minimal UI
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, AreaSeries, CrosshairMode, createSeriesMarkers } from 'lightweight-charts';
import { ChevronDown, X, Search, TrendingUp, TrendingDown, Plus, Minus, BarChart2, Activity, Play, Pause, SkipForward, RotateCcw, Gauge, CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useSocket } from '../../context/SocketContext';
import OrderModal from '../OrderModal';
import { formatDate } from '../../utils/formatters';
import { TradeBoxPrimitive, AVAILABLE_STUDIES, TIMEFRAMES, INTRADAY_INTERVALS, getChartColors, calculateEMA, calculateSMA, calculateWMA, calculateHMA, calculateVWMA, calculateRSI, calculateMACD, calculateStochastic, calculateCCI, calculateWilliamsR, calculateROC, calculateATR, calculateSupertrend, calculateParabolicSAR, calculateADX, calculateVWAP, calculateOBV, calculateIchimoku, calculateBollingerBands } from './hooks/useTradingChart';

const ACT_SPEEDS = [
  { value: 2000, label: '0.5x' },
  { value: 1000, label: '1x' },
  { value: 500,  label: '2x' },
  { value: 250,  label: '4x' },
  { value: 100,  label: '10x' },
];

const TradingChart = ({
  symbol,
  patterns = [],
  onTimeframeChange,
  height = 500,
  className = '',
  showBuySell = true,
  defaultTimeframe = '1d',
  defaultIndicators = [],
  onBuy,
  onSell,
  backtestTrades = [],        // [{entry_date, entry_price, exit_date, exit_price, pnl_pct, exit_reason}]
  backtestConfig = {},        // {stop_loss_pct, target_pct} — used for rectangle overlay
  showTradeMarkers = false,   // when true, render entry/exit lines on candlestick series
  highlightedTradeIndex = null, // index of trade to highlight with shaded zone
  replayUpToDate = null,      // date string "YYYY-MM-DD" — when set, chart renders candles up to this date
  onCandlesLoaded = null,     // callback(dateStrings[]) — fired after candles are fetched
  stretch = false,
  // ── ACT mode props ────────────────────────────────────────────────────────
  actMode = false,            // when true: uses preloaded candles + replay controls; hides timeframe/B/S
  preloadedCandles = null,    // candle array pre-supplied by ACT scenario
  startFromIndex = null,      // replay pauses at this index (question boundary)
  hideOutcome = false,        // hide exit/P&L info bar (ACT question phase)
  onPauseAt = null,           // fired when replay reaches startFromIndex
  onActComplete = null,       // fired when replay finishes all candles
  actEntryPrice = null,
  actExitPrice = null,
  actStopLoss = null,
  actTargetPrice = null,
  actSide = 'BUY',
  actStaticMode = false,      // show all candles without replay animation
  actKeyLevel = null,         // key horizontal level (breakout level, VWAP, ORB high, etc.)
  actKeyLevelLabel = null,    // label shown on price axis for actKeyLevel
}) => {
  const { theme } = useTheme();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const seriesMarkersRef = useRef(null); // createSeriesMarkers instance (LWC v5)
  const tradeBoxPrimRef  = useRef(null); // TradeBoxPrimitive instance
  const volumeSeriesRef = useRef(null);
  const volumeMaSeriesRef = useRef(null);
  const indicatorSeriesRef = useRef({});
  const patternSeriesRef = useRef([]);
  const tradeMarkerLinesRef = useRef([]); // kept for legacy cleanup only
  const mountedRef = useRef(true);
  const candlesRef = useRef([]);
  const indicatorPanelRef = useRef(null);

  const [chartTheme, setChartTheme] = useState(theme);
  const [timeframe, setTimeframe] = useState(defaultTimeframe);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candles, setCandles] = useState([]);
  const [crosshairData, setCrosshairData] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [showIndicatorPanel, setShowIndicatorPanel] = useState(false);
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);
  const [indicatorSearch, setIndicatorSearch] = useState('');
  const [activeIndicators, setActiveIndicators] = useState(defaultIndicators || []);
  const [showPatterns, setShowPatterns] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [chartType, setChartType] = useState('candle'); // 'candle' | 'line'
  const lineSeriesRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(null); // null = 100% (CSS default)
  const dragStateRef = useRef(null); // { startX, startWidth }
  const [showSR, setShowSR] = useState(false);
  const srLinesRef = useRef([]); // createPriceLine handles
  const [showFib, setShowFib] = useState(false);
  const fibLinesRef = useRef([]); // createPriceLine handles for Fibonacci levels
  const [orderModal, setOrderModal] = useState(null); // { orderType: 'BUY'|'SELL' }
  const [crosshairPixel, setCrosshairPixel] = useState(null); // { x, y } in chart container px
  const crosshairDragRef = useRef(false);
  const [activePatterns, setActivePatterns] = useState([]); // selected candlestick pattern keys
  const patternMarkersRef = useRef(null); // createSeriesMarkers instance for candlestick pattern markers

  // ── ACT mode state ────────────────────────────────────────────────────────
  const [actCurrentIndex, setActCurrentIndex] = useState(0);
  const [actDisplayedCandles, setActDisplayedCandles] = useState([]);
  const [actIsPlaying, setActIsPlaying] = useState(false);
  const [actIsFinished, setActIsFinished] = useState(false);
  const [actOutcome, setActOutcome] = useState(null); // 'SUCCESS' | 'FAILED' | null
  const [actSpeed, setActSpeed] = useState(500);
  const [actShowSpeedMenu, setActShowSpeedMenu] = useState(false);
  const actSpeedMenuRef = useRef(null);
  const actIntervalRef = useRef(null);
  const actStartIdxRef = useRef(0);
  const actAllCandlesRef = useRef([]);
  // ACT price lines
  const actEntryLineRef = useRef(null);
  const actExitLineRef = useRef(null);
  const actSlLineRef = useRef(null);
  const actTargetLineRef = useRef(null);
  const actKeyLineRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  const { prices } = useSocket();
  const isDark = chartTheme === 'dark';
  const chartColors = getChartColors(isDark);

  // Sync with app theme initially
  useEffect(() => {
    setChartTheme(theme);
  }, [theme]);

  // Close indicator panel on outside click
  useEffect(() => {
    if (!showIndicatorPanel) return;
    const handler = (e) => {
      if (indicatorPanelRef.current && !indicatorPanelRef.current.contains(e.target)) {
        setShowIndicatorPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showIndicatorPanel]);

  // Component mounted tracking
  useEffect(() => {
    mountedRef.current = true;
    // Suppress the benign ResizeObserver loop notification that lightweight-charts autoSize triggers
    const origErr = window.onerror;
    window.onerror = (msg, ...rest) => {
      if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
      return origErr ? origErr(msg, ...rest) : false;
    };
    return () => {
      mountedRef.current = false;
      window.onerror = origErr;
    };
  }, []);

  // Keep candlesRef in sync so crosshair handler always has current data
  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  // ── ACT mode: initialize from preloadedCandles ────────────────────────────
  useEffect(() => {
    if (!actMode || !preloadedCandles || !preloadedCandles.length) return;
    actAllCandlesRef.current = preloadedCandles;
    setActOutcome(null);
    setActIsFinished(false);
    setActIsPlaying(false);
    const sIdx = startFromIndex != null
      ? Math.min(startFromIndex, preloadedCandles.length - 1)
      : (actStaticMode ? preloadedCandles.length - 1 : preloadedCandles.length - 1);
    actStartIdxRef.current = sIdx;
    setActCurrentIndex(sIdx);
    setActDisplayedCandles(preloadedCandles.slice(0, sIdx + 1));
    setLoading(false);
  }, [actMode, preloadedCandles, startFromIndex, actStaticMode]);

  // ── ACT mode: update chart when actDisplayedCandles changes ──────────────
  useEffect(() => {
    if (!actMode || !candleSeriesRef.current || !actDisplayedCandles.length) return;
    try {
      // LWC treats numeric timestamps as UTC and applies browser timezone for display.
      // Raw epochs from DB are UTC — LWC + IST browser shows correct IST times with no offset needed.
      const fmtC = actDisplayedCandles.map(c => ({
        time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
      }));
      candleSeriesRef.current.setData(fmtC);
      if (volumeSeriesRef.current) {
        volumeSeriesRef.current.setData(actDisplayedCandles.map(c => ({
          time: c.time, value: c.volume || 0,
          color: c.close >= c.open ? chartColors.volumeUp : chartColors.volumeDown,
        })));
        // Push volume panel to bottom 12% so candles get maximum vertical space
        if (chartRef.current) {
          chartRef.current.priceScale('volume').applyOptions({ scaleMargins: { top: 0.88, bottom: 0 } });
        }
      }
      // Indicators on currently displayed candles
      const closes = actDisplayedCandles.map(c => c.close);
      const volumes = actDisplayedCandles.map(c => c.volume || 0);
      // Re-run indicator calculation on displayed slice
      Object.values(indicatorSeriesRef.current).forEach(s => {
        if (s && chartRef.current) { try { chartRef.current.removeSeries(s); } catch (_) {} }
      });
      indicatorSeriesRef.current = {};
      if (activeIndicators.length && chartRef.current) {
        const c2 = chartColors.indicatorColors;
        const addLine2 = (key, data, color, opts = {}) => {
          const s = chartRef.current.addSeries(LineSeries, { color, lineWidth: 1, ...opts });
          s.setData(data.map((v, i) => ({ time: actDisplayedCandles[i].time, value: v })).filter(d => d.value !== null));
          indicatorSeriesRef.current[key] = s;
        };
        activeIndicators.forEach(ind => {
          try {
            switch (ind) {
              case 'ema9':   addLine2('ema9',   calculateEMA(closes, 9),   c2.ema9);   break;
              case 'ema20':  addLine2('ema20',  calculateEMA(closes, 20),  c2.ema20);  break;
              case 'ema50':  addLine2('ema50',  calculateEMA(closes, 50),  c2.ema50);  break;
              case 'ema200': addLine2('ema200', calculateEMA(closes, 200), c2.ema200); break;
              case 'sma20':  addLine2('sma20',  calculateSMA(closes, 20),  c2.sma20);  break;
              case 'sma50':  addLine2('sma50',  calculateSMA(closes, 50),  c2.sma50);  break;
              case 'sma200': addLine2('sma200', calculateSMA(closes, 200), c2.sma200); break;
              case 'vwap':   addLine2('vwap',   calculateVWAP(actDisplayedCandles), c2.vwap); break;
              case 'bollinger': {
                const { upper, middle, lower } = calculateBollingerBands(closes, 20, 2);
                addLine2('bbUpper', upper, c2.bollinger, { lineStyle: 2 });
                addLine2('bbMiddle', middle, '#ab47bc');
                addLine2('bbLower', lower, c2.bollinger, { lineStyle: 2 });
                break;
              }
              default: break;
            }
          } catch (e) { console.warn('ACT indicator error:', ind, e); }
        });
      }
      // Scroll: show all pre-candles with slight right padding
      if (chartRef.current) {
        const total = actDisplayedCandles.length;
        chartRef.current.timeScale().setVisibleLogicalRange({ from: -1, to: total + 1 });
      }
      // Entry marker (index-based, uses IST-adjusted time from fmtC)
      if (actEntryPrice != null) {
        const sIdx = actStartIdxRef.current;
        const entryCandle = fmtC[Math.min(sIdx, fmtC.length - 1)];
        if (entryCandle) {
          try {
            const markers = [{ time: entryCandle.time, position: 'belowBar', color: '#6366F1', shape: 'arrowUp', text: 'Entry', size: 1 }];
            if (actExitPrice != null && fmtC.length > sIdx + 1) {
              const lastC = fmtC[fmtC.length - 1];
              markers.push({ time: lastC.time, position: 'aboveBar', color: '#f59e0b', shape: 'arrowDown', text: 'Exit', size: 1 });
            }
            markers.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
            if (seriesMarkersRef.current) { try { seriesMarkersRef.current.setMarkers(markers); } catch (_) {} }
            else seriesMarkersRef.current = createSeriesMarkers(candleSeriesRef.current, markers);
          } catch (_) {}
        }
      }
    } catch (e) { console.warn('ACT chart update error:', e); }
  }, [actMode, actDisplayedCandles, activeIndicators]); // eslint-disable-line

  // ── ACT mode: manage price lines for SL/Target/Entry/Exit ────────────────
  useEffect(() => {
    if (!actMode || !candleSeriesRef.current) return;
    const removeLine = (ref) => {
      if (ref.current) { try { candleSeriesRef.current.removePriceLine(ref.current); } catch (_) {} ref.current = null; }
    };
    const addLine = (ref, price, color, style = 0) => {
      if (price == null) { removeLine(ref); return; }
      if (ref.current) { try { ref.current.applyOptions({ price }); } catch (_) {} return; }
      try {
        ref.current = candleSeriesRef.current.createPriceLine({ price, color, lineWidth: 2, lineStyle: style, axisLabelVisible: true });
      } catch (_) {}
    };
    addLine(actEntryLineRef, actEntryPrice, '#6366F1');
    addLine(actExitLineRef,  actExitPrice,  '#f59e0b', 0);
    addLine(actSlLineRef,    actStopLoss,   '#ef4444');
    addLine(actTargetLineRef, actTargetPrice, '#16C784');
    // Key level line — dashed amber, with custom axis label
    if (actKeyLevel != null) {
      if (actKeyLineRef.current) {
        try { actKeyLineRef.current.applyOptions({ price: actKeyLevel }); } catch (_) {}
      } else {
        try {
          actKeyLineRef.current = candleSeriesRef.current.createPriceLine({
            price: actKeyLevel,
            color: '#f97316',
            lineWidth: 1,
            lineStyle: 2, // dashed
            axisLabelVisible: true,
            title: actKeyLevelLabel || 'Key Level',
          });
        } catch (_) {}
      }
    } else if (actKeyLineRef.current) {
      try { candleSeriesRef.current.removePriceLine(actKeyLineRef.current); } catch (_) {}
      actKeyLineRef.current = null;
    }
  }, [actMode, actEntryPrice, actExitPrice, actStopLoss, actTargetPrice, actKeyLevel, actKeyLevelLabel, actDisplayedCandles]);

  // ── ACT replay advance ────────────────────────────────────────────────────
  const actAdvanceCandle = useCallback(() => {
    const all = actAllCandlesRef.current;
    setActCurrentIndex(prev => {
      if (prev >= all.length - 1) {
        setActIsPlaying(false);
        setActIsFinished(true);
        if (onActComplete) onActComplete();
        return prev;
      }
      const next = prev + 1;
      const newCandle = all[next];

      // Pause at startFromIndex boundary
      if (startFromIndex != null && next >= startFromIndex && prev < startFromIndex) {
        setActIsPlaying(false);
        if (onPauseAt) onPauseAt();
        setActDisplayedCandles(all.slice(0, next + 1));
        return next;
      }

      // Outcome detection
      setActOutcome(cur => {
        if (cur) return cur;
        if (actTargetPrice != null && newCandle.high >= actTargetPrice) { setActIsPlaying(false); return 'SUCCESS'; }
        if (actStopLoss    != null && newCandle.low  <= actStopLoss)    { setActIsPlaying(false); return 'FAILED'; }
        return cur;
      });

      setActDisplayedCandles(all.slice(0, next + 1));
      return next;
    });
  }, [startFromIndex, onPauseAt, onActComplete, actTargetPrice, actStopLoss]);

  useEffect(() => {
    if (!actMode) return;
    if (actIsPlaying) actIntervalRef.current = setInterval(actAdvanceCandle, actSpeed);
    else clearInterval(actIntervalRef.current);
    return () => clearInterval(actIntervalRef.current);
  }, [actMode, actIsPlaying, actSpeed, actAdvanceCandle]);

  useEffect(() => {
    if (!actShowSpeedMenu) return;
    const onClickOutside = (e) => {
      if (actSpeedMenuRef.current && !actSpeedMenuRef.current.contains(e.target)) setActShowSpeedMenu(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [actShowSpeedMenu]);

  const actHandlePlayPause = () => actIsPlaying ? setActIsPlaying(false) : (!actIsFinished && setActIsPlaying(true));
  const actHandleNext = () => { setActIsPlaying(false); actAdvanceCandle(); };
  const actHandleReset = () => {
    setActIsPlaying(false);
    setActIsFinished(false);
    setActOutcome(null);
    const s = actStartIdxRef.current;
    const all = actAllCandlesRef.current;
    setActCurrentIndex(s);
    if (all.length) setActDisplayedCandles(all.slice(0, s + 1));
  };

  const actProgress = actAllCandlesRef.current.length > 1
    ? Math.round((actCurrentIndex / (actAllCandlesRef.current.length - 1)) * 100)
    : 0;

  // Fetch OHLCV data
  const fetchData = useCallback(async () => {
    if (!symbol || !mountedRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chart/${symbol}?interval=${timeframe}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      // Intraday 404 = no live data yet (market closed or ingest not run)
      // Show empty chart with banner instead of error
      if (response.status === 404 && INTRADAY_INTERVALS.includes(timeframe)) {
        if (mountedRef.current) setCandles([]);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch chart data');

      const data = await response.json();
      if (mountedRef.current) {
        const fetched = data.candles || [];
        setCandles(fetched);
        setIsLive(!!data.is_intraday && fetched.length > 0);
        if (onCandlesLoaded && fetched.length) {
          onCandlesLoaded(fetched.map(c => String(c.time)));
        }
      }
    } catch (err) {
      console.error('Chart data fetch error:', err);
      if (mountedRef.current) {
        setError('Failed to load chart data');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [symbol, timeframe, API_URL]);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { type: 'solid', color: chartColors.background },
        textColor: chartColors.text,
        fontFamily: "'Inter', sans-serif",
        fontSize: 12,
        fontStyle: '',
      },
      grid: {
        vertLines: { color: chartColors.grid, style: 1 },
        horzLines: { color: chartColors.grid, style: 1 },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { visible: false, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: {
        borderColor: chartColors.grid,
        scaleMargins: { top: 0.1, bottom: 0.25 },
        minimumWidth: 60,
        fontSize: 11,
      },
      timeScale: {
        borderColor: chartColors.grid,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 8,
        tickMarkFormatter: (time, tickMarkType) => {
          const date = typeof time === 'number' ? new Date(time * 1000) : new Date(time);
          // tickMarkType 3 = Time, 4 = TimeWithSeconds â€" show HH:MM in IST
          if (typeof time === 'number' && tickMarkType >= 3) {
            return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
          }
          return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
        },
      },
      localization: {
        locale: 'en-IN',
        dateFormat: 'dd/MM/yyyy',
        timeFormatter: (time) => {
          if (typeof time === 'string') {
            // daily/weekly/monthly â€" date string "YYYY-MM-DD", no time component
            const [y, m, d] = time.split('-');
            return formatDate(new Date(+y, +m - 1, +d));
          }
          // intraday â€" Unix seconds â†' show date + IST time
          const date = new Date(time * 1000);
          return date.toLocaleString('en-IN', {
            day: '2-digit', month: 'short',
            hour: '2-digit', minute: '2-digit',
            hour12: false, timeZone: 'Asia/Kolkata'
          });
        },
      },
    });

    // Create candlestick series â€" ₹ formatter on the series itself, not globally
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: chartColors.upColor,
      downColor: chartColors.downColor,
      borderDownColor: chartColors.downColor,
      borderUpColor: chartColors.upColor,
      wickDownColor: chartColors.downColor,
      wickUpColor: chartColors.upColor,
      priceFormat: {
        type: 'custom',
        formatter: (price) => '₹' + price.toFixed(2),
        minMove: 0.01,
      },
    });

    // Volume formatter â€" Indian notation (Cr / L / K)
    const volumeFormatter = (val) => {
      if (val >= 1e7)  return (val / 1e7).toFixed(2)  + ' Cr';
      if (val >= 1e5)  return (val / 1e5).toFixed(2)  + ' L';
      if (val >= 1e3)  return (val / 1e3).toFixed(1)  + ' K';
      return Math.round(val).toString();
    };

    // Create volume series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: chartColors.volumeUp,
      priceFormat: { type: 'custom', formatter: volumeFormatter, minMove: 1 },
      priceScaleId: 'volume',
    });

    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.75, bottom: 0 },
      borderVisible: true,
      borderColor: isDark ? '#3d4663' : '#9CA3AF',
      visible: true,
      textColor: isDark ? '#6b7280' : '#9CA3AF',
    });

    // Volume MA (20-period) overlay on volume panel
    const volumeMaSeries = chart.addSeries(LineSeries, {
      color: '#f59e0b',
      lineWidth: 1,
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });
    volumeMaSeriesRef.current = volumeMaSeries;

    // Crosshair subscription — update OHLCV data and circle pixel position
    chart.subscribeCrosshairMove((param) => {
      if (!mountedRef.current) return;
      // Always update pixel from LWC's own crosshair position
      if (param.point) {
        setCrosshairPixel({ x: param.point.x, y: param.point.y });
      }
      if (param.time && param.seriesData.size > 0) {
        const candleData = param.seriesData.get(candleSeries);
        const volumeData = param.seriesData.get(volumeSeries);
        if (candleData) {
          const paramTimeStr = typeof param.time === 'object'
            ? `${param.time.year}-${String(param.time.month).padStart(2,'0')}-${String(param.time.day).padStart(2,'0')}`
            : String(param.time);
          const match = candlesRef.current.find(c => String(c.time) === paramTimeStr);
          setCrosshairData({
            time: param.time,
            open: candleData.open,
            high: candleData.high,
            low: candleData.low,
            close: candleData.close,
            volume: match?.volume ?? volumeData?.value ?? 0,
          });
        }
      }
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    return () => {
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      volumeMaSeriesRef.current = null;
      lineSeriesRef.current = null;
      indicatorSeriesRef.current = {};
      patternSeriesRef.current = [];
      chartRef.current = null;
      chart.remove();
    };
  }, [chartTheme, height, isDark, chartColors.background, chartColors.text, chartColors.grid, chartColors.crosshair, chartColors.upColor, chartColors.downColor, chartColors.volumeUp]);

  // Handle expand/collapse
  useEffect(() => {
    document.body.style.overflow = isExpanded ? 'hidden' : '';
    // autoSize fires on the CSS change but the container may not have settled yet.
    // Force one explicit sync after layout completes.
    const timer = setTimeout(() => {
      if (chartContainerRef.current && chartRef.current) {
        const { clientWidth, clientHeight } = chartContainerRef.current;
        if (clientWidth > 0 && clientHeight > 0) {
          chartRef.current.applyOptions({ width: clientWidth, height: clientHeight });
        }
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  // Update chart data (skip in ACT mode — uses preloadedCandles)
  useEffect(() => {
    if (actMode) return;
    fetchData();
  }, [fetchData, actMode]);

  // Poll for new candles every 60s (new candle appears each minute)
  useEffect(() => {
    if (actMode) return;
    if (!['1m', '5m', '15m', '1h'].includes(timeframe)) return;
    let timer = null;
    const checkAndPoll = async () => {
      try {
        const res = await fetch(`${API_URL}/system/mode`);
        const data = await res.json();
        if (data.mode === 'LIVE-S') {
          timer = setInterval(fetchData, 60_000);
        }
      } catch {}
    };
    checkAndPoll();
    return () => { if (timer) clearInterval(timer); };
  }, [timeframe, fetchData, API_URL]);

  // Tick the last candle from socket prices — no polling, zero extra requests
  useEffect(() => {
    if (!isLive || !symbol || !['1m', '5m', '15m', '1h'].includes(timeframe)) return;
    if (!candleSeriesRef.current || !candlesRef.current.length || !volumeSeriesRef.current) return;
    const sym = symbol.endsWith('.NS') ? symbol : `${symbol}.NS`;
    const tick = prices[sym] ?? prices[symbol.replace('.NS', '')];
    if (!tick?.price) return;
    const livePrice = tick.price;
    const last = candlesRef.current[candlesRef.current.length - 1];
    const updated = {
      time:  last.time,
      open:  last.open,
      high:  Math.max(last.high, livePrice),
      low:   Math.min(last.low,  livePrice),
      close: livePrice,
    };
    try { candleSeriesRef.current.update(updated); } catch (_) {}
    try { volumeSeriesRef.current.update({ time: last.time, value: last.volume,
      color: updated.close >= updated.open ? chartColors.volumeUp : chartColors.volumeDown }); } catch (_) {}
    setCrosshairData(d => d?.time === last.time ? { ...d, close: livePrice, high: updated.high, low: updated.low } : d);
  }, [prices, isLive, symbol, timeframe, chartColors.volumeUp, chartColors.volumeDown]);

  // Update series data
  useEffect(() => {
    if (!candleSeriesRef.current || !candles.length || !chartRef.current) return;

    try {
      const displayCandles = replayUpToDate
        ? candles.filter(c => String(c.time) <= replayUpToDate)
        : candles;

      if (!displayCandles.length) return;

      const formattedCandles = displayCandles.map(c => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }));

      // Show/hide candle vs line series based on chartType
      if (chartType === 'line') {
        candleSeriesRef.current.applyOptions({ visible: false });
        if (!lineSeriesRef.current && chartRef.current) {
          lineSeriesRef.current = chartRef.current.addSeries(LineSeries, {
            color: '#42a5f5',
            lineWidth: 2,
            priceFormat: { type: 'custom', formatter: (p) => '₹' + p.toFixed(2), minMove: 0.01 },
            lastValueVisible: true,
            priceLineVisible: false,
          });
        }
        if (lineSeriesRef.current) {
          lineSeriesRef.current.applyOptions({ visible: true });
          lineSeriesRef.current.setData(displayCandles.map(c => ({ time: c.time, value: c.close })));
        }
      } else {
        candleSeriesRef.current.applyOptions({ visible: true });
        candleSeriesRef.current.setData(formattedCandles);
        // Hide line series without removing it - avoids re-add on next toggle
        if (lineSeriesRef.current) {
          lineSeriesRef.current.applyOptions({ visible: false });
        }
      }

      // Volume bars
      if (volumeSeriesRef.current) {
        const volumeData = displayCandles.map(c => ({
          time: c.time,
          value: c.volume,
          color: c.close >= c.open ? chartColors.volumeUp : chartColors.volumeDown,
        }));
        volumeSeriesRef.current.setData(volumeData);
      }

      // Volume MA (20-period SMA)
      if (volumeMaSeriesRef.current) {
        const vols = displayCandles.map(c => c.volume || 0);
        const period = 20;
        const maData = displayCandles.map((c, i) => {
          if (i < period - 1) return null;
          const avg = vols.slice(i - period + 1, i + 1).reduce((s, v) => s + v, 0) / period;
          return { time: c.time, value: avg };
        }).filter(Boolean);
        volumeMaSeriesRef.current.setData(maData);
      }

      // Set latest candle data + initialize circle position at last candle
      if (displayCandles.length > 0 && mountedRef.current) {
        const latest = displayCandles[displayCandles.length - 1];
        setCrosshairData({
          time: latest.time,
          open: latest.open,
          high: latest.high,
          low: latest.low,
          close: latest.close,
          volume: latest.volume,
        });
        // Position circle at last candle after LWC finishes rendering
        setTimeout(() => {
          if (!mountedRef.current || !chartRef.current || !candleSeriesRef.current || !chartContainerRef.current) return;
          try {
            const ts = chartRef.current.timeScale();
            const x = ts.timeToCoordinate(latest.time);
            const y = candleSeriesRef.current.priceToCoordinate(latest.close);
            if (x != null && y != null) setCrosshairPixel({ x, y });
          } catch (_) {}
        }, 150);
      }

      if (replayUpToDate) {
        // In replay mode: keep the last ~120 candles visible, right-anchored
        const n = displayCandles.length;
        const from = displayCandles[Math.max(0, n - 120)]?.time;
        const to   = displayCandles[n - 1]?.time;
        if (from && to) chartRef.current.timeScale().setVisibleRange({ from, to });
      } else if (!INTRADAY_INTERVALS.includes(timeframe) && candles.length > 300) {
        // For daily+ with lots of data, show last ~1 year
        const from = candles[candles.length - 260]?.time;
        const to   = candles[candles.length - 1]?.time;
        if (from && to) {
          chartRef.current.timeScale().setVisibleRange({ from, to });
        } else {
          chartRef.current.timeScale().fitContent();
        }
      } else {
        chartRef.current.timeScale().fitContent();
      }
    } catch (e) {
      console.warn('Chart data update error:', e);
    }
  }, [candles, replayUpToDate, timeframe, chartType, chartColors.volumeUp, chartColors.volumeDown]);

  // Auto Support & Resistance lines
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    srLinesRef.current.forEach(pl => { try { candleSeriesRef.current.removePriceLine(pl); } catch (_) {} });
    srLinesRef.current = [];
    const _srSrc = actMode ? actDisplayedCandles : candles;
    if (!showSR || _srSrc.length < 10) return;

    const src = _srSrc.slice(-Math.min(_srSrc.length, 300));
    const left = 3, right = 3; // pivot window
    const pivotHighs = [], pivotLows = [];
    for (let i = left; i < src.length - right; i++) {
      const h = src[i].high, l = src[i].low;
      const isHigh = src.slice(i - left, i).every(c => c.high <= h) && src.slice(i + 1, i + right + 1).every(c => c.high <= h);
      const isLow  = src.slice(i - left, i).every(c => c.low  >= l) && src.slice(i + 1, i + right + 1).every(c => c.low  >= l);
      if (isHigh) pivotHighs.push(h);
      if (isLow)  pivotLows.push(l);
    }

    // Cluster nearby pivots (within 0.3% of each other) → take median
    const cluster = (prices, pct = 0.003) => {
      const sorted = [...prices].sort((a, b) => a - b);
      const clusters = [];
      sorted.forEach(p => {
        const last = clusters[clusters.length - 1];
        if (last && Math.abs(p - last[last.length - 1]) / last[0] < pct) last.push(p);
        else clusters.push([p]);
      });
      return clusters
        .filter(c => c.length >= 2) // needs 2+ touches to be meaningful
        .map(c => c[Math.floor(c.length / 2)]) // median
        .slice(-6); // max 6 lines each
    };

    const supports    = cluster(pivotLows);
    const resistances = cluster(pivotHighs);
    const currentPrice = src[src.length - 1]?.close || 0;

    supports.forEach(price => {
      try {
        const pl = candleSeriesRef.current.createPriceLine({
          price,
          color: price < currentPrice ? '#26a69a' : '#ef5350',
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: 'S',
        });
        srLinesRef.current.push(pl);
      } catch (_) {}
    });
    resistances.forEach(price => {
      try {
        const pl = candleSeriesRef.current.createPriceLine({
          price,
          color: price > currentPrice ? '#ef5350' : '#26a69a',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'R',
        });
        srLinesRef.current.push(pl);
      } catch (_) {}
    });
  }, [showSR, candles, actMode, actDisplayedCandles]);

  // Auto Fibonacci Retracement — swing high/low of visible candles
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    fibLinesRef.current.forEach(pl => { try { candleSeriesRef.current.removePriceLine(pl); } catch (_) {} });
    fibLinesRef.current = [];
    const _fibSrc = actMode ? actDisplayedCandles : candles;
    if (!showFib || _fibSrc.length < 5) return;

    const src = _fibSrc.slice(-Math.min(_fibSrc.length, 200));
    const high = Math.max(...src.map(c => c.high));
    const low  = Math.min(...src.map(c => c.low));
    const diff = high - low;

    const FIB_LEVELS = [
      { pct: 0,     label: '0%',     color: '#ef4444' },
      { pct: 0.236, label: '23.6%',  color: '#f59e0b' },
      { pct: 0.382, label: '38.2%',  color: '#eab308' },
      { pct: 0.5,   label: '50%',    color: '#a3e635' },
      { pct: 0.618, label: '61.8%',  color: '#16C784' },
      { pct: 0.786, label: '78.6%',  color: '#22d3ee' },
      { pct: 1,     label: '100%',   color: '#6366f1' },
    ];

    FIB_LEVELS.forEach(({ pct, label, color }) => {
      const price = high - diff * pct;
      try {
        const pl = candleSeriesRef.current.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle: 1, // dotted
          axisLabelVisible: true,
          title: `Fib ${label}`,
        });
        fibLinesRef.current.push(pl);
      } catch (_) {}
    });
  }, [showFib, candles, actMode, actDisplayedCandles]);

  // Pattern overlay — draw price level + neckline using createPriceLine()
  // Same approach as TradeReplay: full-width horizontal lines that always
  // span the chart and touch the price axis. No time coordinates needed.
  useEffect(() => {
    if (!chartRef.current || !candleSeriesRef.current) return;

    // Remove existing pattern price lines
    patternSeriesRef.current.forEach(pl => {
      try { candleSeriesRef.current.removePriceLine(pl); } catch (_) {}
    });
    patternSeriesRef.current = [];

    if (!showPatterns || !patterns.length || !candles.length) return;

    const bullishTypes = [
      'double_bottom', 'triple_bottom', 'support_bounce',
      'inverse_head_and_shoulders', 'ascending_triangle', 'bull_flag'
    ];
    const isBullish = (type) => bullishTypes.includes(type);

    patterns.forEach((p) => {
      const color = isBullish(p.pattern_type) ? '#26a69a' : '#ef5350';
      const label = p.pattern_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const tag   = `${label}${p.confidence === 'high' ? ' â˜…' : ''}`;

      // Pattern level line â€" solid, with axis label
      const levelLine = candleSeriesRef.current.createPriceLine({
        price:            p.price_level,
        color,
        lineWidth:        3,
        lineStyle:        0, // solid
        axisLabelVisible: true,
        title:            tag,
      });
      patternSeriesRef.current.push(levelLine);

      // Neckline â€" dashed, labelled separately
      if (p.neckline) {
        const neckLine = candleSeriesRef.current.createPriceLine({
          price:            p.neckline,
          color,
          lineWidth:        2,
          lineStyle:        1, // dashed
          axisLabelVisible: true,
          title:            'Neckline',
        });
        patternSeriesRef.current.push(neckLine);
      }
    });
  }, [patterns, candles, showPatterns]);


  // Render all trade boxes via canvas primitive + entry/exit markers for highlighted trade
  useEffect(() => {
    if (!candleSeriesRef.current || !candles.length || !showTradeMarkers) return;

    // Detach old primitive
    if (tradeBoxPrimRef.current) {
      try { candleSeriesRef.current.detachPrimitive(tradeBoxPrimRef.current); } catch (_) {}
      tradeBoxPrimRef.current = null;
    }

    if (!backtestTrades.length) return;

    const isDailyCandle = typeof candles[0].time === 'string';
    const slPct = backtestConfig.stop_loss_pct || 5;
    const tpPct = backtestConfig.target_pct    || 10;

    const snapToCandle = (dateStr) => {
      if (!dateStr) return null;
      const d = dateStr.substring(0, 10);
      if (isDailyCandle) {
        return candles.find(c => c.time === d)?.time ||
          candles.reduce((cl, c) => Math.abs(new Date(c.time) - new Date(d)) < Math.abs(new Date(cl.time) - new Date(d)) ? c : cl).time;
      }
      const ts = Math.floor(new Date(d + 'T00:00:00Z').getTime() / 1000) + (5.5 * 3600);
      return candles.reduce((cl, c) => Math.abs(c.time - ts) < Math.abs(cl.time - ts) ? c : cl).time;
    };

    // Last displayed candle time â€" used as right edge of in-progress (active) boxes
    const lastCandleTime = replayUpToDate
      ? (candles.filter(c => String(c.time) <= replayUpToDate).at(-1)?.time ?? candles.at(-1)?.time)
      : candles.at(-1)?.time;

    // Determine which trades to render and whether each is complete or still active
    // Date-replay mode: show trades whose entry_date <= replayUpToDate
    // Trade-index mode (legacy): show trades up to highlightedTradeIndex
    // Overview (both null): show all trades
    let tradesToRender;
    if (replayUpToDate !== null) {
      tradesToRender = backtestTrades.filter(t => t.entry_date <= replayUpToDate);
    } else if (highlightedTradeIndex !== null) {
      tradesToRender = backtestTrades.slice(0, highlightedTradeIndex + 1);
    } else {
      tradesToRender = backtestTrades;
    }

    const boxes = tradesToRender.map((trade, idx) => {
      const entryTime = snapToCandle(trade.entry_date);
      if (!entryTime) return null;

      // In date-replay: trade is "active" if it has entered but not yet exited
      const isActive = replayUpToDate !== null && trade.exit_date > replayUpToDate;
      const exitTime = isActive
        ? (lastCandleTime ?? snapToCandle(trade.exit_date))
        : snapToCandle(trade.exit_date);
      if (!exitTime) return null;

      const entry = trade.entry_price;
      // Determine the highlight: in date-replay use highlightedTradeIndex passed from parent
      const originalIdx = backtestTrades.indexOf(trade);
      return {
        entryTime,
        exitTime,
        entryPrice: entry,
        slPrice:    entry * (1 - slPct / 100),
        tpPrice:    entry * (1 + tpPct / 100),
        isWin:      (trade.pnl_pct || 0) >= 0,
        pnlPct:     trade.pnl_pct || 0,
        isActive,
        highlighted: originalIdx === highlightedTradeIndex,
      };
    }).filter(Boolean);

    // Attach primitive
    const prim = new TradeBoxPrimitive(boxes, isDark);
    tradeBoxPrimRef.current = prim;
    try { candleSeriesRef.current.attachPrimitive(prim); } catch (e) {
      console.error('[TradingChart] attachPrimitive failed:', e);
    }

    // Entry/exit markers for highlighted trade only (skip for active trades â€" no exit yet)
    if (highlightedTradeIndex != null) {
      const box = boxes.find(b => b.highlighted);
      if (box && !box.isActive) {
        const markers = [
          { time: box.entryTime, position: 'belowBar', color: '#2196F3', shape: 'arrowUp',   text: 'ENTRY', size: 2 },
          { time: box.exitTime,  position: 'aboveBar', color: '#FF9800', shape: 'arrowDown', text: 'EXIT',  size: 2 },
        ];
        markers.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        try { seriesMarkersRef.current = createSeriesMarkers(candleSeriesRef.current, markers); } catch (_) {}
      } else {
        // Active trade: only show ENTRY marker
        if (box) {
          try { seriesMarkersRef.current = createSeriesMarkers(candleSeriesRef.current, [
            { time: box.entryTime, position: 'belowBar', color: '#2196F3', shape: 'arrowUp', text: 'ENTRY', size: 2 },
          ]); } catch (_) {}
        }
      }
    } else {
      try { seriesMarkersRef.current = createSeriesMarkers(candleSeriesRef.current, []); } catch (_) {}
    }

    return () => {
      if (tradeBoxPrimRef.current && candleSeriesRef.current) {
        try { candleSeriesRef.current.detachPrimitive(tradeBoxPrimRef.current); } catch (_) {}
        tradeBoxPrimRef.current = null;
      }
    };
  }, [highlightedTradeIndex, replayUpToDate, backtestTrades, backtestConfig, candles, showTradeMarkers]);

  // Update indicators
  useEffect(() => {
    if (!chartRef.current || !candles.length) return;

    const closes = candles.map(c => c.close);
    const volumes = candles.map(c => c.volume || 0);

    // Remove existing indicators
    Object.values(indicatorSeriesRef.current).forEach(series => {
      if (series && chartRef.current) {
        try { chartRef.current.removeSeries(series); } catch (e) {}
      }
    });
    indicatorSeriesRef.current = {};

    const c = chartColors.indicatorColors;
    const addLine = (key, data, color, opts = {}) => {
      const s = chartRef.current.addSeries(LineSeries, { color, lineWidth: 1, ...opts });
      s.setData(data.map((v, i) => ({ time: candles[i].time, value: v })).filter(d => d.value !== null));
      indicatorSeriesRef.current[key] = s;
    };
    const oscScale = (key) => ({ priceScaleId: key, lastValueVisible: true, priceLineVisible: false });

    activeIndicators.forEach(indicator => {
      if (!chartRef.current) return;
      try {
        switch (indicator) {
          case 'ema9':   addLine('ema9',   calculateEMA(closes, 9),   c.ema9,   {}); break;
          case 'ema20':  addLine('ema20',  calculateEMA(closes, 20),  c.ema20,  {}); break;
          case 'ema50':  addLine('ema50',  calculateEMA(closes, 50),  c.ema50,  {}); break;
          case 'ema200': addLine('ema200', calculateEMA(closes, 200), c.ema200, {}); break;
          case 'sma20':  addLine('sma20',  calculateSMA(closes, 20),  c.sma20,  {}); break;
          case 'sma50':  addLine('sma50',  calculateSMA(closes, 50),  c.sma50,  {}); break;
          case 'sma200': addLine('sma200', calculateSMA(closes, 200), c.sma200, {}); break;
          case 'vwma':   addLine('vwma',   calculateVWMA(closes, volumes, 20), c.vwma, {}); break;
          case 'hma':    addLine('hma',    calculateHMA(closes, 20),  c.hma,    {}); break;
          case 'vwap':   addLine('vwap',   calculateVWAP(candles),    c.vwap,   {}); break;

          case 'bollinger': {
            const { upper, middle, lower } = calculateBollingerBands(closes, 20, 2);
            addLine('bbUpper',  upper,  c.bollinger, { lineStyle: 2 });
            addLine('bbMiddle', middle, '#ab47bc',   {});
            addLine('bbLower',  lower,  c.bollinger, { lineStyle: 2 });
            break;
          }
          case 'atr': addLine('atr', calculateATR(candles, 14), c.atr, oscScale('atr')); break;

          case 'rsi': {
            addLine('rsi', calculateRSI(closes, 14), c.rsi, oscScale('rsi'));
            chartRef.current.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.75, bottom: 0 }, borderVisible: false });
            break;
          }
          case 'macd': {
            const { macdLine, signal, histogram } = calculateMACD(closes);
            addLine('macdLine',   macdLine,  c.macd,       oscScale('macd'));
            addLine('macdSignal', signal,    c.macdSignal, oscScale('macd'));
            addLine('macdHist',   histogram, c.macdHist,   oscScale('macd'));
            chartRef.current.priceScale('macd').applyOptions({ scaleMargins: { top: 0.80, bottom: 0 }, borderVisible: false });
            break;
          }
          case 'stochastic': {
            const { k, d } = calculateStochastic(candles);
            addLine('stochK', k, c.stochK, oscScale('stoch'));
            addLine('stochD', d, c.stochD, oscScale('stoch'));
            chartRef.current.priceScale('stoch').applyOptions({ scaleMargins: { top: 0.75, bottom: 0 }, borderVisible: false });
            break;
          }
          case 'cci':       addLine('cci',      calculateCCI(candles, 20),       c.cci,      oscScale('cci'));      break;
          case 'williamsr': addLine('williamsr', calculateWilliamsR(candles, 14), c.williamsr,oscScale('williamsr')); break;
          case 'roc':       addLine('roc',       calculateROC(closes, 12),        c.roc,      oscScale('roc'));       break;
          case 'adx':       addLine('adx',       calculateADX(candles, 14),       c.adx,      oscScale('adx'));       break;
          case 'obv':       addLine('obv',       calculateOBV(candles),           c.obv,      oscScale('obv'));       break;
          case 'volumesma': addLine('volumesma', calculateSMA(volumes, 20),       c.volumesma,{ priceScaleId: 'volume' }); break;

          case 'supertrend': {
            const st = calculateSupertrend(candles, 10, 3);
            const stSeries = chartRef.current.addSeries(LineSeries, { lineWidth: 2, lastValueVisible: false });
            stSeries.setData(st.map((v, i) => ({ time: candles[i].time, value: v.value, color: v.trend === 1 ? '#76ff03' : '#ff1744' })));
            indicatorSeriesRef.current.supertrend = stSeries;
            break;
          }
          case 'parabolicsar': {
            const sar = calculateParabolicSAR(candles);
            addLine('parabolicsar', sar, c.parabolicsar, { lineStyle: 3, lineWidth: 0, pointMarkersVisible: true });
            break;
          }
          case 'ichimoku': {
            const ich = calculateIchimoku(candles);
            addLine('ichTenkan',  ich.map(v => v.tenkan),  '#e91e63', {});
            addLine('ichKijun',   ich.map(v => v.kijun),   '#2196f3', {});
            addLine('ichSenkouA', ich.map(v => v.senkouA), '#4caf50', { lineStyle: 2 });
            addLine('ichSenkouB', ich.map(v => v.senkouB), '#f44336', { lineStyle: 2 });
            break;
          }
          default: break;
        }
      } catch (e) {
        console.warn('Indicator error:', indicator, e);
      }
    });
  }, [activeIndicators, candles, chartColors.indicatorColors]);

  // Handle timeframe change
  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    setShowTimeframeDropdown(false);
    if (onTimeframeChange) onTimeframeChange(tf);
  };

  // Toggle indicator
  const toggleIndicator = (key) => {
    setActiveIndicators(prev => 
      prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key]
    );
    setShowIndicatorPanel(false);
  };

  // Toggle chart theme
  const toggleChartTheme = () => {
    setChartTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };


  // Format volume
  const formatVolume = (vol) => {
    if (!vol) return '0';
    if (vol >= 10000000) return (vol / 10000000).toFixed(2) + ' Cr';
    if (vol >= 100000) return (vol / 100000).toFixed(2) + ' L';
    if (vol >= 1000) return (vol / 1000).toFixed(2) + ' K';
    return vol.toFixed(0);
  };

  // Price calculations — in ACT mode derive from actDisplayedCandles
  const _priceSource = actMode ? actDisplayedCandles : candles;
  const currentPrice = _priceSource.length > 0 ? _priceSource[_priceSource.length - 1].close : 0;
  const prevPrice = _priceSource.length > 1 ? _priceSource[_priceSource.length - 2].close : currentPrice;
  const priceChange = currentPrice - prevPrice;
  const priceChangePct = prevPrice > 0 ? ((priceChange / prevPrice) * 100) : 0;
  const isPositive = priceChange >= 0;

  // ── Candlestick pattern definitions ───────────────────────────────────────────
  const CANDLESTICK_PATTERNS = [
    { key: 'bullish_engulfing',   label: 'Bullish Engulfing',    direction: 'bullish' },
    { key: 'bearish_engulfing',   label: 'Bearish Engulfing',    direction: 'bearish' },
    { key: 'hammer',              label: 'Hammer',               direction: 'bullish' },
    { key: 'shooting_star',       label: 'Shooting Star',        direction: 'bearish' },
    { key: 'doji',                label: 'Doji',                 direction: 'neutral' },
    { key: 'morning_star',        label: 'Morning Star',         direction: 'bullish' },
    { key: 'evening_star',        label: 'Evening Star',         direction: 'bearish' },
    { key: 'piercing_line',       label: 'Piercing Line',        direction: 'bullish' },
    { key: 'dark_cloud_cover',    label: 'Dark Cloud Cover',     direction: 'bearish' },
    { key: 'spinning_top',        label: 'Spinning Top',         direction: 'neutral' },
    { key: 'marubozu_bull',       label: 'Marubozu (Bullish)',   direction: 'bullish' },
    { key: 'marubozu_bear',       label: 'Marubozu (Bearish)',   direction: 'bearish' },
    { key: 'bullish_harami',      label: 'Bullish Harami',       direction: 'bullish' },
    { key: 'bearish_harami',      label: 'Bearish Harami',       direction: 'bearish' },
    { key: 'three_white_soldiers',label: 'Three White Soldiers', direction: 'bullish' },
  ];

  // Detects a given pattern across the candle array; returns array of matching candle times
  const detectPattern = useCallback((key, candleArr) => {
    const matches = [];
    const n = candleArr.length;
    for (let i = 2; i < n; i++) {
      const c = candleArr[i];
      const p = candleArr[i - 1];
      const pp = candleArr[i - 2];
      const cBody = Math.abs(c.close - c.open);
      const cRange = c.high - c.low;
      const pBody = Math.abs(p.close - p.open);
      const pRange = p.high - p.low;
      const ppBody = Math.abs(pp.close - pp.open);
      const cBull = c.close > c.open;
      const pBull = p.close > p.open;
      const ppBull = pp.close > pp.open;

      let hit = false;
      switch (key) {
        case 'bullish_engulfing':
          hit = !pBull && cBull && c.open < p.close && c.close > p.open && cBody > pBody * 0.8;
          break;
        case 'bearish_engulfing':
          hit = pBull && !cBull && c.open > p.close && c.close < p.open && cBody > pBody * 0.8;
          break;
        case 'hammer': {
          const lowerWick = Math.min(c.open, c.close) - c.low;
          const upperWick = c.high - Math.max(c.open, c.close);
          hit = cRange > 0 && lowerWick >= cBody * 2 && upperWick <= cBody * 0.3 && cBody >= cRange * 0.1;
          break;
        }
        case 'shooting_star': {
          const upperWick = c.high - Math.max(c.open, c.close);
          const lowerWick = Math.min(c.open, c.close) - c.low;
          hit = cRange > 0 && upperWick >= cBody * 2 && lowerWick <= cBody * 0.3 && cBody >= cRange * 0.1;
          break;
        }
        case 'doji':
          hit = cRange > 0 && cBody / cRange < 0.1;
          break;
        case 'morning_star':
          hit = !ppBull && ppBody > 0 && pBody < ppBody * 0.4 && cBull && c.close > (pp.open + pp.close) / 2;
          break;
        case 'evening_star':
          hit = ppBull && ppBody > 0 && pBody < ppBody * 0.4 && !cBull && c.close < (pp.open + pp.close) / 2;
          break;
        case 'piercing_line':
          hit = !pBull && cBull && c.open < p.low && c.close > (p.open + p.close) / 2 && c.close < p.open;
          break;
        case 'dark_cloud_cover':
          hit = pBull && !cBull && c.open > p.high && c.close < (p.open + p.close) / 2 && c.close > p.open;
          break;
        case 'spinning_top':
          hit = cRange > 0 && cBody / cRange < 0.3 && (c.high - Math.max(c.open, c.close)) > cBody * 0.5 && (Math.min(c.open, c.close) - c.low) > cBody * 0.5;
          break;
        case 'marubozu_bull':
          hit = cBull && cRange > 0 && cBody / cRange > 0.92;
          break;
        case 'marubozu_bear':
          hit = !cBull && cRange > 0 && cBody / cRange > 0.92;
          break;
        case 'bullish_harami':
          hit = !pBull && cBull && c.open > p.close && c.close < p.open && cBody < pBody * 0.6;
          break;
        case 'bearish_harami':
          hit = pBull && !cBull && c.open < p.close && c.close > p.open && cBody < pBody * 0.6;
          break;
        case 'three_white_soldiers':
          hit = ppBull && pBull && cBull && p.open > pp.open && p.open < pp.close && c.open > p.open && c.open < p.close && c.close > p.close && ppBody > 0 && pBody > 0 && cBody > 0;
          break;
        default:
          break;
      }
      if (hit) matches.push(c.time);
    }
    return matches;
  }, []);

  // Apply pattern markers whenever activePatterns or candles change
  useEffect(() => {
    if (!candleSeriesRef.current || !candlesRef.current.length) return;
    // Remove previous pattern markers
    if (patternMarkersRef.current) {
      try { patternMarkersRef.current.setMarkers([]); } catch (_) {}
    }
    if (!activePatterns.length) return;

    const allMarkers = [];
    for (const key of activePatterns) {
      const patDef = CANDLESTICK_PATTERNS.find(p => p.key === key);
      if (!patDef) continue;
      const isBull = patDef.direction === 'bullish';
      const isBear = patDef.direction === 'bearish';
      const color  = isBull ? '#16C784' : isBear ? '#ef4444' : '#f59e0b';
      const times  = detectPattern(key, candlesRef.current);
      for (const t of times) {
        allMarkers.push({
          time: t,
          position: isBull || patDef.direction === 'neutral' ? 'belowBar' : 'aboveBar',
          color,
          shape: isBull ? 'arrowUp' : isBear ? 'arrowDown' : 'circle',
          text: patDef.label,
          size: 1,
        });
      }
    }
    // Sort by time (required by LWC)
    allMarkers.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
    try {
      if (patternMarkersRef.current) {
        patternMarkersRef.current.setMarkers(allMarkers);
      } else {
        patternMarkersRef.current = createSeriesMarkers(candleSeriesRef.current, allMarkers);
      }
    } catch (_) {}
  }, [activePatterns, candles, detectPattern]); // eslint-disable-line

  const togglePattern = (key) => {
    setActivePatterns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Filtered studies
  const filteredStudies = AVAILABLE_STUDIES.filter(s =>
    s.label.toLowerCase().includes(indicatorSearch.toLowerCase())
  );

  // Zoom via LWC barSpacing
  const handleZoomIn  = () => {
    if (!chartRef.current) return;
    try { const ts = chartRef.current.timeScale(); const cur = ts.options().barSpacing || 8; ts.applyOptions({ barSpacing: Math.min(cur * 1.3, 50) }); } catch (_) {}
  };
  const handleZoomOut = () => {
    if (!chartRef.current) return;
    try { const ts = chartRef.current.timeScale(); const cur = ts.options().barSpacing || 8; ts.applyOptions({ barSpacing: Math.max(cur / 1.3, 3) }); } catch (_) {}
  };

  return (
    <>
    <style>{`@keyframes chart-live-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.3;transform:scale(1.6)} }`}</style>
    <div
      className={`relative flex flex-col ${isExpanded ? 'w-full' : 'rounded-xl overflow-hidden'} ${className}`}
      style={{
        backgroundColor: chartColors.background,
        height: stretch ? '100%' : isExpanded ? '100svh' : actMode ? `${height}px` : `clamp(260px, 42svh, ${height}px)`,
        width: isExpanded ? '100%' : chartWidth ? `${chartWidth}px` : '100%',
        maxWidth: isExpanded ? '100%' : chartWidth ? `${chartWidth}px` : '100%',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        border: `1px solid ${chartColors.grid}`,
        ...(isExpanded && { position: 'fixed', inset: 0, zIndex: 9999, borderRadius: 0, overflow: 'hidden' }),
      }}
      data-testid="trading-chart"
    >
      {/* Sideways drag handle — right edge, only when not expanded */}
      {!isExpanded && (
        <div
          title="Drag to resize width"
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { if (!dragStateRef.current) e.currentTarget.style.opacity = '0'; }}
          onMouseDown={e => {
            e.preventDefault();
            const containerEl = e.currentTarget.parentElement;
            dragStateRef.current = { startX: e.clientX, startWidth: containerEl.offsetWidth };
            e.currentTarget.style.opacity = '1';
            const onMove = mv => {
              if (!dragStateRef.current) return;
              const delta = mv.clientX - dragStateRef.current.startX;
              const newW = Math.max(300, dragStateRef.current.startWidth + delta);
              setChartWidth(newW);
            };
            const onUp = () => {
              dragStateRef.current = null;
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
          style={{
            position: 'absolute', top: 0, right: -4, width: 8, height: '100%',
            cursor: 'ew-resize', zIndex: 20, opacity: 0, transition: 'opacity 0.15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{
            width: 3, height: 32, borderRadius: 4,
            backgroundColor: isDark ? '#4a5568' : '#9CA3AF',
          }} />
        </div>
      )}
      {/* ACT mode replay controls */}
      {actMode && !actStaticMode && (
        <div className="flex items-center justify-between px-2 py-1.5 border-b gap-2 flex-shrink-0"
             style={{ borderColor: chartColors.grid, backgroundColor: chartColors.toolbarBg }}>
          {/* Play controls */}
          <div className="flex items-center gap-1.5">
            <button onClick={actHandlePlayPause}
                    className="p-1.5 rounded-lg hover:opacity-80"
                    style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}>
              {actIsPlaying
                ? <Pause className="w-3.5 h-3.5" style={{ color: chartColors.text }} />
                : <Play  className="w-3.5 h-3.5" style={{ color: chartColors.text }} />}
            </button>
            <button onClick={actHandleNext}
                    disabled={actCurrentIndex >= actAllCandlesRef.current.length - 1}
                    className="p-1.5 rounded-lg hover:opacity-80 disabled:opacity-40"
                    style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}>
              <SkipForward className="w-3.5 h-3.5" style={{ color: chartColors.text }} />
            </button>
            <button onClick={actHandleReset}
                    className="p-1.5 rounded-lg hover:opacity-80"
                    style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}>
              <RotateCcw className="w-3.5 h-3.5" style={{ color: chartColors.text }} />
            </button>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 text-[11px]">
            {[
              { label: 'Entry',  color: '#6366F1', show: actEntryPrice != null },
              { label: 'Exit',   color: '#f59e0b', show: actExitPrice  != null },
              { label: 'SL',     color: '#ef4444', show: actStopLoss   != null },
              { label: 'Target', color: '#16C784', show: actTargetPrice != null },
            ].filter(x => x.show).map(({ label, color }) => (
              <span key={label} className="flex items-center gap-1">
                <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundColor: color }} />
                <span style={{ color: chartColors.text }}>{label}</span>
              </span>
            ))}
          </div>

          {/* Outcome badge */}
          <div className="flex items-center gap-2">
            {actOutcome === 'SUCCESS' && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-500/20 text-green-400">
                <CheckCircle className="w-3 h-3" /><span>Target Hit</span>
              </span>
            )}
            {actOutcome === 'FAILED' && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400">
                <XCircle className="w-3 h-3" /><span>SL Hit</span>
              </span>
            )}
            {/* Speed */}
            <div className="relative" ref={actSpeedMenuRef}>
              <button onClick={() => setActShowSpeedMenu(v => !v)}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded"
                      style={{ color: chartColors.text }}>
                <Gauge className="w-3 h-3" />
                <span>{ACT_SPEEDS.find(s => s.value === actSpeed)?.label || '1x'}</span>
              </button>
              {actShowSpeedMenu && (
                <div className="absolute top-full right-0 mt-1 rounded-lg shadow-lg overflow-hidden z-10"
                     style={{ backgroundColor: chartColors.background, border: `1px solid ${chartColors.grid}` }}>
                  {ACT_SPEEDS.map(({ value, label }) => (
                    <button key={value}
                            onClick={() => { setActSpeed(value); setActShowSpeedMenu(false); }}
                            className="block w-full px-3 py-1 text-[10px] text-right whitespace-nowrap"
                            style={{
                              backgroundColor: actSpeed === value ? '#42a5f5' : 'transparent',
                              color: actSpeed === value ? '#fff' : chartColors.text,
                            }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="text-[10px]" style={{ color: chartColors.text }}>
              {actCurrentIndex + 1}/{actAllCandlesRef.current.length} ({actProgress}%)
            </span>
          </div>
        </div>
      )}

      {/* ACT progress bar */}
      {actMode && !actStaticMode && (
        <div className="h-0.5 flex-shrink-0" style={{ backgroundColor: chartColors.grid }}>
          <div className="h-full transition-all duration-100"
               style={{ width: `${actProgress}%`, backgroundColor: '#42a5f5' }} />
        </div>
      )}

      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-2 py-1.5 border-b gap-2 flex-shrink-0"
        style={{ borderColor: chartColors.grid, backgroundColor: chartColors.toolbarBg }}
      >
        {/* Left: Indicator icon + Symbol/Price */}
        <div className="flex items-center gap-1 min-w-0 flex-1 leading-none">
          {/* S&R toggle */}
          <button
            onClick={() => setShowSR(v => !v)}
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold hover:opacity-80 transition-opacity flex-shrink-0"
            style={{
              color: showSR ? '#fff' : chartColors.text,
              backgroundColor: showSR ? '#7c3aed' : 'transparent',
              border: `1px solid ${showSR ? '#7c3aed' : chartColors.grid}`,
            }}
            title="Auto Support & Resistance"
          >
            S&amp;R
          </button>

          {/* Fibonacci toggle */}
          <button
            onClick={() => setShowFib(v => !v)}
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold hover:opacity-80 transition-opacity flex-shrink-0"
            style={{
              color: showFib ? '#fff' : chartColors.text,
              backgroundColor: showFib ? '#d97706' : 'transparent',
              border: `1px solid ${showFib ? '#d97706' : chartColors.grid}`,
            }}
            title="Fibonacci Retracement (auto swing high/low)"
          >
            Fib
          </button>

          {/* Indicator icon button */}
          <div className="relative flex-shrink-0" ref={indicatorPanelRef}>
            <button
              onClick={() => setShowIndicatorPanel(!showIndicatorPanel)}
              className="p-2 sm:p-1 rounded hover:opacity-80 transition-opacity"
              style={{ color: showIndicatorPanel ? '#42a5f5' : chartColors.text }}
              data-testid="chart-patterns-btn"
              title="Indicators"
            >
              <BarChart2 className="w-3.5 h-3.5" />
            </button>

            {/* Indicator Panel */}
            {showIndicatorPanel && (
              <div 
                className="absolute top-full left-0 mt-1 w-56 rounded-lg shadow-xl z-50" 
                style={{ backgroundColor: chartColors.dropdownBg, border: `1px solid ${chartColors.grid}` }}
              >
                <div className="p-2 border-b" style={{ borderColor: chartColors.grid }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#42a5f5' }}>Active Indicators</div>
                  {activeIndicators.length === 0 ? (
                    <div className="text-xs" style={{ color: chartColors.text }}>None selected</div>
                  ) : (
                    <div className="space-y-1">
                      {activeIndicators.map(ind => {
                        const study = AVAILABLE_STUDIES.find(s => s.key === ind);
                        return (
                          <div key={ind} className="flex items-center justify-between text-xs py-1 px-2 rounded" style={{ backgroundColor: isDark ? '#2a2e39' : '#F3F4F6' }}>
                            <span style={{ color: isDark ? '#fff' : '#111827' }}>{study?.label}</span>
                            <button onClick={(e) => { e.stopPropagation(); toggleIndicator(ind); }} className="hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="p-2">
                  <div className="text-xs font-semibold mb-2" style={{ color: '#42a5f5' }}>Add Indicator</div>
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: chartColors.text }} />
                    <input
                      type="text"
                      placeholder="Search"
                      value={indicatorSearch}
                      onChange={(e) => setIndicatorSearch(e.target.value)}
                      className="w-full pl-7 pr-2 py-1.5 text-xs rounded border"
                      style={{ backgroundColor: isDark ? '#2a2e39' : '#F9FAFB', borderColor: chartColors.grid, color: isDark ? '#fff' : '#111827' }}
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-0.5">
                    {filteredStudies.map(study => (
                      <button
                        key={study.key}
                        onClick={() => toggleIndicator(study.key)}
                        className="w-full text-left text-xs py-1.5 px-2 rounded hover:opacity-70 transition-opacity"
                        style={{ color: activeIndicators.includes(study.key) ? '#42a5f5' : chartColors.text }}
                      >
                        {study.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Candlestick Patterns section */}
                <div className="p-2 border-t" style={{ borderColor: chartColors.grid }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#f59e0b' }}>Candlestick Patterns</div>
                  {activePatterns.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {activePatterns.map(key => {
                        const pd = CANDLESTICK_PATTERNS.find(p => p.key === key);
                        const col = pd?.direction === 'bullish' ? '#16C784' : pd?.direction === 'bearish' ? '#ef4444' : '#f59e0b';
                        return (
                          <span key={key} className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${col}22`, color: col }}>
                            {pd?.label}
                            <button onClick={() => togglePattern(key)} className="hover:opacity-60"><X className="w-2.5 h-2.5" /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="max-h-36 overflow-y-auto space-y-0.5">
                    {CANDLESTICK_PATTERNS.filter(p => !indicatorSearch || p.label.toLowerCase().includes(indicatorSearch.toLowerCase())).map(pd => {
                      const col = pd.direction === 'bullish' ? '#16C784' : pd.direction === 'bearish' ? '#ef4444' : '#f59e0b';
                      const active = activePatterns.includes(pd.key);
                      return (
                        <button
                          key={pd.key}
                          onClick={() => togglePattern(pd.key)}
                          className="w-full text-left text-xs py-1.5 px-2 rounded hover:opacity-70 transition-opacity flex items-center gap-1.5"
                          style={{ color: active ? col : chartColors.text }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: active ? col : chartColors.grid, flexShrink: 0, display: 'inline-block' }} />
                          {pd.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Symbol + Price + % + live dot */}
          <div className="flex items-center gap-1 min-w-0 overflow-hidden leading-none">
            <span className="font-bold text-xs truncate leading-none" style={{ color: isDark ? '#fff' : '#111827' }}>{symbol?.replace('.NS', '')}</span>
            <span className="font-semibold text-xs flex-shrink-0 leading-none" style={{ color: isPositive ? chartColors.upColor : chartColors.downColor }}>
              ₹{currentPrice.toFixed(2)}
            </span>
            <span className="text-xs flex-shrink-0 leading-none" style={{ color: isPositive ? chartColors.upColor : chartColors.downColor }}>
              ({isPositive ? '+' : ''}{priceChangePct.toFixed(2)}%)
            </span>
            {isLive && (
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#26a69a', display: 'inline-block', flexShrink: 0,
                animation: 'chart-live-pulse 1.2s ease-in-out infinite' }} title="Live sim" />
            )}
          </div>
        </div>

        {/* Right: Expand only */}
        <button
          onClick={() => setIsExpanded(v => !v)}
          className="p-2.5 sm:p-1.5 rounded-lg hover:opacity-80 transition-opacity flex-shrink-0"
          style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}
          title={isExpanded ? 'Exit fullscreen' : 'Expand chart'}
          data-testid="chart-expand-toggle"
        >
          {isExpanded ? (
            <svg className="w-3.5 h-3.5" style={{ color: chartColors.text }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
              <line x1="10" y1="14" x2="3" y2="21" /><line x1="21" y1="3" x2="14" y2="10" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" style={{ color: chartColors.text }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          )}
        </button>
      </div>

      {/* Active Indicators + Pattern Labels */}
      {(activeIndicators.length > 0 || activePatterns.length > 0) && (
        <div className="absolute top-14 right-3 z-10 flex flex-col items-end space-y-0.5">
          {activeIndicators.map(ind => {
            const study = AVAILABLE_STUDIES.find(s => s.key === ind);
            const color = chartColors.indicatorColors[ind];
            return (
              <div key={ind} className="flex items-center space-x-1 text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}20`, color }}>
                <span>{study?.label}</span>
                <button onClick={() => toggleIndicator(ind)} className="hover:opacity-70 rounded p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          {activePatterns.map(key => {
            const pd = CANDLESTICK_PATTERNS.find(p => p.key === key);
            const col = pd?.direction === 'bullish' ? '#16C784' : pd?.direction === 'bearish' ? '#ef4444' : '#f59e0b';
            return (
              <div key={key} className="flex items-center space-x-1 text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${col}20`, color: col }}>
                <span>{pd?.label}</span>
                <button onClick={() => togglePattern(key)} className="hover:opacity-70 rounded p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Chart Container */}
      <div className="relative flex-1 min-h-0">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: chartColors.background }}>
            <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: '#42a5f5', borderTopColor: 'transparent' }} />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: chartColors.background }}>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}
        <div ref={chartContainerRef} data-testid="chart-container" className="relative w-full h-full" />

        {/* OHLCV Overlay - top-left, constrained to chart width minus price axis (~60px) */}
        {crosshairData && (
          <div className="absolute top-2 left-2 z-10 font-mono pointer-events-none"
            style={{
              right: 64, // leave room for price axis
              backgroundColor: isDark ? 'rgba(10,14,23,0.75)' : 'rgba(255,255,255,0.75)',
              borderRadius: 4, padding: '2px 6px',
              display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '6px',
              overflow: 'hidden', minWidth: 0,
            }}>
            {[
              { label: 'O', value: crosshairData.open?.toFixed(2), color: isDark ? '#e5e7eb' : '#111827' },
              { label: 'H', value: crosshairData.high?.toFixed(2), color: chartColors.upColor },
              { label: 'L', value: crosshairData.low?.toFixed(2),  color: chartColors.downColor },
              { label: 'C', value: crosshairData.close?.toFixed(2), color: crosshairData.close >= crosshairData.open ? chartColors.upColor : chartColors.downColor, bold: true },
              { label: 'V', value: formatVolume(crosshairData.volume), color: isDark ? '#e5e7eb' : '#111827' },
            ].map(({ label, value, color, bold }) => (
              <span key={label} style={{ fontSize: 'clamp(9px, 2vw, 11px)', whiteSpace: 'nowrap', color: isDark ? '#9ca3af' : '#6b7280', flexShrink: 0 }}>
                {label}:<span style={{ color, fontWeight: bold ? 700 : 400, marginLeft: 2 }}>{value}</span>
              </span>
            ))}
          </div>
        )}
        
        {/* Crosshair drag circle — sits at the crosshair intersection, drag it to scrub OHLCV */}
        {/* Custom crosshair lines — always in sync with circle position */}
        {crosshairPixel && (
          <>
            <div style={{ position: 'absolute', left: crosshairPixel.x, top: 0, bottom: 0, width: 1, backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)', pointerEvents: 'none', zIndex: 10 }} />
            <div style={{ position: 'absolute', top: crosshairPixel.y, left: 0, right: 0, height: 1, backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)', pointerEvents: 'none', zIndex: 10 }} />
          </>
        )}

        {crosshairPixel && (
          <div
            style={{
              position: 'absolute',
              left: crosshairPixel.x - 11,
              top: crosshairPixel.y - 11,
              width: 22, height: 22,
              borderRadius: '50%',
              border: '2px solid #42a5f5',
              backgroundColor: 'rgba(66,165,245,0.18)',
              cursor: 'grab',
              zIndex: 15,
              touchAction: 'none',
              boxSizing: 'border-box',
            }}
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              crosshairDragRef.current = true;
              const startDrag = (getXY) => {
                const container = chartContainerRef.current;
                const chart = chartRef.current;
                const series = candleSeriesRef.current;
                if (!container || !chart || !series) return;
                const fireAt = (clientX, clientY) => {
                  const rect = container.getBoundingClientRect();
                  const x = Math.max(0, Math.min(clientX - rect.left, rect.width - 1));
                  const y = Math.max(0, Math.min(clientY - rect.top, rect.height - 1));
                  setCrosshairPixel({ x, y });
                  // Resolve the candle at this x coordinate directly
                  try {
                    const ts = chart.timeScale();
                    const logical = ts.coordinateToLogical(x);
                    if (logical == null) return;
                    const idx = Math.round(logical);
                    const candles = candlesRef.current;
                    const candle = candles[Math.max(0, Math.min(idx, candles.length - 1))];
                    if (candle) setCrosshairData({ time: candle.time, open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume });
                  } catch (_) {}
                };
                return fireAt;
              };
              const fireAt = startDrag();
              if (!fireAt) return;
              fireAt(e.clientX, e.clientY);
              const onMove = mv => fireAt(mv.clientX, mv.clientY);
              const onUp = () => {
                crosshairDragRef.current = false;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}
            onTouchStart={e => {
              e.stopPropagation();
              e.preventDefault();
              crosshairDragRef.current = true;
              const container = chartContainerRef.current;
              const chart = chartRef.current;
              if (!container || !chart) return;
              const fireAt = (clientX, clientY) => {
                const rect = container.getBoundingClientRect();
                const x = Math.max(0, Math.min(clientX - rect.left, rect.width - 1));
                const y = Math.max(0, Math.min(clientY - rect.top, rect.height - 1));
                setCrosshairPixel({ x, y });
                try {
                  const ts = chart.timeScale();
                  const logical = ts.coordinateToLogical(x);
                  if (logical == null) return;
                  const idx = Math.round(logical);
                  const candles = candlesRef.current;
                  const candle = candles[Math.max(0, Math.min(idx, candles.length - 1))];
                  if (candle) setCrosshairData({ time: candle.time, open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume });
                } catch (_) {}
              };
              const t = e.touches[0];
              if (t) fireAt(t.clientX, t.clientY);
              const onMove = mv => { const touch = mv.touches[0]; if (touch) fireAt(touch.clientX, touch.clientY); };
              const onEnd = () => {
                crosshairDragRef.current = false;
                document.removeEventListener('touchmove', onMove);
                document.removeEventListener('touchend', onEnd);
              };
              document.addEventListener('touchmove', onMove, { passive: false });
              document.addEventListener('touchend', onEnd);
            }}
          />
        )}

        {/* VOL label â€" sits just above the LWC volume scale border (scale top = 75% from chart top = 25% from bottom) */}
        <div
          className="absolute left-1 pointer-events-none"
          style={{ bottom: 'calc(25% + 4px)' }}
        >
          <span className="text-[12px] font-semibold tracking-widest uppercase" style={{ color: isDark ? '#4a5568' : '#9CA3AF' }}>
            VOL
          </span>
        </div>
        
      </div>

      {/* Intraday banner */}
      {INTRADAY_INTERVALS.includes(timeframe) && (
        <div
          className="px-3 py-1 text-xs text-center flex-shrink-0"
          style={{ backgroundColor: isDark ? '#1c2a1c' : '#f0fdf4', color: isDark ? '#4ade80' : '#166534', borderTop: `1px solid ${chartColors.grid}` }}
        >
          {candles.length === 0
            ? 'No live data - ingest runs during NSE market hours (9:15 AM – 3:30 PM IST)'
            : `Showing last ${timeframe === '1m' ? '3' : timeframe === '5m' ? '5' : timeframe === '15m' ? '7' : '10'} trading days`}
        </div>
      )}

      {/* ACT info bar — entry / exit / P&L / R:R */}
      {actMode && (
        <div className="px-3 py-2 border-t flex-shrink-0" style={{ borderColor: chartColors.grid, backgroundColor: chartColors.toolbarBg }}>
          <div className={`grid ${hideOutcome ? 'grid-cols-1' : 'grid-cols-4'} gap-3`}>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: chartColors.text }}>Entry</p>
              <p className="text-xs font-bold" style={{ color: '#6366F1' }}>
                {actEntryPrice != null ? `₹${actEntryPrice.toFixed(2)}` : '—'}
              </p>
            </div>
            {!hideOutcome && (<>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: chartColors.text }}>Exit</p>
                <p className="text-xs font-bold" style={{ color: actExitPrice != null ? '#f59e0b' : chartColors.text }}>
                  {actExitPrice != null ? `₹${actExitPrice.toFixed(2)}` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: chartColors.text }}>P&amp;L</p>
                {actEntryPrice != null && actExitPrice != null ? (() => {
                  const pnl = actSide === 'BUY'
                    ? (actExitPrice - actEntryPrice) / actEntryPrice * 100
                    : (actEntryPrice - actExitPrice) / actEntryPrice * 100;
                  const pos = pnl >= 0;
                  return <p className="text-xs font-bold" style={{ color: pos ? '#16C784' : '#ef4444' }}>{pos ? '+' : ''}{pnl.toFixed(2)}%</p>;
                })() : <p className="text-xs font-bold" style={{ color: chartColors.text }}>—</p>}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: chartColors.text }}>Risk / Reward</p>
                {actStopLoss != null && actTargetPrice != null && actEntryPrice != null ? (() => {
                  const rr = Math.abs((actTargetPrice - actEntryPrice) / (actEntryPrice - actStopLoss));
                  return (
                    <>
                      <p className="text-xs font-bold" style={{ color: chartColors.upColor }}>1 : {rr.toFixed(1)}</p>
                      <p className="text-[10px] mt-0.5">
                        <span style={{ color: '#ef4444' }}>SL ₹{actStopLoss.toFixed(2)}</span>
                        <span style={{ color: chartColors.text }}> · </span>
                        <span style={{ color: '#16C784' }}>Tgt ₹{actTargetPrice.toFixed(2)}</span>
                      </p>
                    </>
                  );
                })() : <p className="text-xs font-bold" style={{ color: chartColors.text }}>—</p>}
              </div>
            </>)}
          </div>
        </div>
      )}

      {/* Bottom Bar: Timeframes (left) + Zoom (center) + B/S (right) */}
      {!actMode && <div className="flex items-center gap-1 px-2 py-1.5 border-t flex-shrink-0" style={{ borderColor: chartColors.grid, backgroundColor: chartColors.toolbarBg }}>
        {/* Timeframe Quick Select */}
        <div className="flex items-center gap-0.5 flex-1">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf.key}
              onClick={() => handleTimeframeChange(tf.key)}
              className="px-1.5 py-0.5 text-[12px] rounded transition-colors"
              style={{
                color: timeframe === tf.key ? '#fff' : chartColors.text,
                backgroundColor: timeframe === tf.key ? '#42a5f5' : 'transparent'
              }}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={handleZoomOut} className="w-6 h-6 flex items-center justify-center rounded hover:opacity-70" style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}>
            <Minus className="w-3 h-3" style={{ color: chartColors.text }} />
          </button>
          <button onClick={handleZoomIn} className="w-6 h-6 flex items-center justify-center rounded hover:opacity-70" style={{ backgroundColor: isDark ? '#2a2e39' : '#E5E7EB' }}>
            <Plus className="w-3 h-3" style={{ color: chartColors.text }} />
          </button>
        </div>

        {/* B / S quick order buttons — always visible */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          <button
            onClick={() => onBuy ? onBuy() : setOrderModal({ orderType: 'BUY' })}
            className="w-7 h-6 flex items-center justify-center rounded font-bold text-[11px]"
            style={{ backgroundColor: '#16C78433', color: '#16C784', border: '1px solid #16C78466' }}
            data-testid="chart-buy-btn"
            title="Buy"
          >
            B
          </button>
          <button
            onClick={() => onSell ? onSell() : setOrderModal({ orderType: 'SELL' })}
            className="w-7 h-6 flex items-center justify-center rounded font-bold text-[11px]"
            style={{ backgroundColor: '#EF444433', color: '#EF4444', border: '1px solid #EF444466' }}
            data-testid="chart-sell-btn"
            title="Sell"
          >
            S
          </button>
        </div>
      </div>}

      {/* Pattern Indicators */}
      {!actMode && patterns.length > 0 && (
        <div className="px-3 py-2 border-t flex items-center space-x-2 overflow-x-auto" style={{ borderColor: chartColors.grid, backgroundColor: chartColors.toolbarBg }}>
          <span className="text-xs whitespace-nowrap" style={{ color: '#42a5f5' }}>Patterns:</span>
          {patterns.map((pattern, idx) => (
            <div 
              key={idx}
              className="flex items-center space-x-1 px-2 py-1 rounded text-xs whitespace-nowrap"
              style={{ backgroundColor: 'rgba(66, 165, 245, 0.1)' }}
            >
              {pattern.pattern_type.includes('bottom') || pattern.pattern_type.includes('bounce') 
                ? <TrendingUp className="w-3 h-3 text-green-500" /> 
                : <TrendingDown className="w-3 h-3 text-red-500" />}
              <span style={{ color: isDark ? '#fff' : '#111827' }}>
                {pattern.pattern_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
              <span style={{ color: chartColors.text }}>@ ₹{pattern.price_level}</span>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Internal order modal — opened by B/S buttons when no onBuy/onSell prop */}
    {orderModal && (
      <OrderModal
        isOpen={true}
        onClose={() => setOrderModal(null)}
        stock={{
          symbol: symbol,
          current_price: crosshairData?.close || candles[candles.length - 1]?.close || 0,
        }}
        orderType={orderModal.orderType}
      />
    )}
    </>
  );
};

export default TradingChart;
